/**
 * EL SERVIDOR DE BOLSILLO: contesta una consulta de PostgREST con la copia local.
 *
 * Sin red, cada pantalla sigue pidiendo sus datos como siempre
 * (`supabase.from("pest_scouting_sessions").select("*, greenhouse:greenhouses(name)")
 * .eq("company_id", X).gte("date", D).order("date", { ascending: false }).limit(50)`).
 * Esa petición llega aquí convertida en su URL (`pest_scouting_sessions?select=...`),
 * y aquí se resuelve igual que lo haría PostgREST, pero sobre las filas que la
 * descarga completa dejó en el teléfono (`espejoOffline`). Así cualquier
 * pantalla —con cualquier fecha, filtro o ficha de detalle— funciona sin red
 * sin haberla tocado, y con TODA la historia, no solo con lo que se vio.
 *
 * Lo que entiende (lo que la app usa, medido el 10-10-2026):
 *   · select: columnas, `*`, alias (`a:col`), relaciones incrustadas a cualquier
 *     profundidad (`alias:tabla!pista!inner(...)`), por nombre de tabla, de
 *     columna o de clave foránea, hacia arriba (objeto) y hacia abajo (lista);
 *   · filtros: eq, neq, gt, gte, lt, lte, like, ilike, is, in, cs, con `not.`,
 *     y `or=(...)` / `and=(...)` anidados; filtros sobre relaciones (`rel.col=`);
 *   · order (con nullsfirst/nullslast), limit, offset, Range;
 *   · `.single()` (Accept objeto) y los conteos (`Prefer: count=exact`, HEAD).
 *
 * LA REGLA DE ORO: ante cualquier cosa que no entienda (`->`, `::`, `...`,
 * agregados, una relación ambigua, un operador raro) devuelve `null` —«no sé»—,
 * y la red contesta con la última copia exacta o con el error de siempre.
 * Nunca se inventa un resultado.
 *
 * Es puro: no sabe de IndexedDB ni de red. Recibe las filas de cada tabla.
 */
import { RELACIONES, type ClaveForanea } from "./relaciones.generated";

export type Fila = Record<string, unknown>;

/* ─── 1. El select ───────────────────────────────────────────────────────── */

export interface Columna {
  tipo: "columna";
  nombre: string;
  alias: string;
  /** Camino dentro de un JSON: `geometry->geoPolygonWgs84`, `datos->>nombre`. */
  json?: Array<{ clave: string; comoTexto: boolean }>;
}
export interface Todas {
  tipo: "todas";
}
export interface Incrustada {
  tipo: "incrustada";
  /** Lo que se escribió antes del paréntesis: tabla, columna o clave foránea. */
  nombre: string;
  alias: string;
  pistas: string[];
  interior: boolean;
  hijos: ElementoSelect[];
}
export type ElementoSelect = Columna | Todas | Incrustada;

class NoSe extends Error {
  constructor(motivo: string) {
    super(motivo);
  }
}

/** Parte por comas de primer nivel (respetando paréntesis y comillas). */
function partirNivel(s: string): string[] {
  const out: string[] = [];
  let prof = 0;
  let comillas = false;
  let actual = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"' && s[i - 1] !== "\\") comillas = !comillas;
    if (!comillas) {
      if (c === "(") prof++;
      else if (c === ")") prof--;
      else if (c === "," && prof === 0) {
        out.push(actual);
        actual = "";
        continue;
      }
    }
    actual += c;
  }
  if (prof !== 0 || comillas) throw new NoSe("paréntesis o comillas sin cerrar");
  out.push(actual);
  return out;
}

export function analizarSelect(select: string): ElementoSelect[] {
  const s = select.replace(/\s+/g, "");
  if (s === "") return [{ tipo: "todas" }];
  return partirNivel(s).map((parte) => {
    if (parte === "*") return { tipo: "todas" } as Todas;
    if (parte.startsWith("...")) throw new NoSe("spread");
    if (parte.includes("::")) throw new NoSe("cast en select");
    const abre = parte.indexOf("(");
    if (abre >= 0) {
      if (!parte.endsWith(")")) throw new NoSe("incrustada mal cerrada");
      const cabeza = parte.slice(0, abre);
      const dentro = parte.slice(abre + 1, -1);
      let alias = "";
      let resto = cabeza;
      const dos = cabeza.indexOf(":");
      if (dos >= 0) {
        alias = cabeza.slice(0, dos);
        resto = cabeza.slice(dos + 1);
      }
      const [nombre, ...pistasCrudas] = resto.split("!");
      const interior = pistasCrudas.includes("inner");
      const pistas = pistasCrudas.filter((p) => p !== "inner" && p !== "left");
      if (!nombre) throw new NoSe("incrustada sin nombre");
      // `rel(count)` es el conteo antiguo de PostgREST, no una columna: no se sabe.
      if (/^(count|sum|avg|min|max)(\(\))?$/.test(dentro)) throw new NoSe("agregado");
      const hijos = analizarSelect(dentro);
      return { tipo: "incrustada", nombre, alias: alias || nombre, pistas, interior, hijos } as Incrustada;
    }
    let alias = "";
    let nombre = parte;
    const dos = parte.indexOf(":");
    if (dos >= 0) {
      alias = parte.slice(0, dos);
      nombre = parte.slice(dos + 1);
    }
    if (/\(\)$/.test(nombre)) throw new NoSe("agregado");
    let json: Columna["json"];
    if (nombre.includes("->")) {
      const trozos = nombre.split(/(->>|->)/);
      nombre = trozos[0];
      json = [];
      for (let i = 1; i < trozos.length; i += 2) {
        const clave = trozos[i + 1];
        if (!clave || !/^[A-Za-z0-9_]+$/.test(clave)) throw new NoSe(`camino json raro: ${parte}`);
        json.push({ clave, comoTexto: trozos[i] === "->>" });
      }
      if (json.slice(0, -1).some((p) => p.comoTexto)) throw new NoSe("->> en medio del camino");
    }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(nombre)) throw new NoSe(`columna rara: ${nombre}`);
    // Sin alias, PostgREST nombra el resultado de un camino json por su última clave.
    const porDefecto = json?.length ? json[json.length - 1].clave : nombre;
    return { tipo: "columna", nombre, alias: alias || porDefecto, json } as Columna;
  });
}

/* ─── 2. Los filtros ─────────────────────────────────────────────────────── */

type Op = "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "like" | "ilike" | "is" | "in" | "cs";
const OPS = new Set<Op>(["eq", "neq", "gt", "gte", "lt", "lte", "like", "ilike", "is", "in", "cs"]);

export type Filtro =
  | { tipo: "cond"; columna: string; op: Op; valor: string; negado: boolean }
  | { tipo: "logico"; y: boolean; negado: boolean; partes: Filtro[] };

function analizarCondicion(columna: string, expr: string): Filtro {
  let negado = false;
  let e = expr;
  if (e.startsWith("not.")) {
    negado = true;
    e = e.slice(4);
  }
  const punto = e.indexOf(".");
  if (punto < 0) throw new NoSe(`filtro sin operador: ${expr}`);
  const op = e.slice(0, punto) as Op;
  let valor = e.slice(punto + 1);
  if (!OPS.has(op)) throw new NoSe(`operador no soportado: ${op}`);
  // Un valor con caracteres reservados viaja entre comillas: `name.eq."a,b"`.
  if (op !== "in" && op !== "cs" && valor.length >= 2 && valor.startsWith('"') && valor.endsWith('"')) {
    valor = valor.slice(1, -1).replace(/\\(.)/g, "$1");
  }
  return { tipo: "cond", columna, op, valor, negado };
}

/** `(a.eq.1,b.is.null,and(c.eq.2,d.neq.3))` → partes. */
function analizarLogico(y: boolean, negado: boolean, lista: string): Filtro {
  if (!lista.startsWith("(") || !lista.endsWith(")")) throw new NoSe("lógico sin paréntesis");
  const partes = partirNivel(lista.slice(1, -1)).map((p): Filtro => {
    const m = p.match(/^(not\.)?(and|or)(\(.*\))$/);
    if (m) return analizarLogico(m[2] === "and", !!m[1], m[3]);
    const punto = p.indexOf(".");
    if (punto < 0) throw new NoSe(`condición rara: ${p}`);
    return analizarCondicion(p.slice(0, punto), p.slice(punto + 1));
  });
  return { tipo: "logico", y, negado, partes };
}

/* ─── 3. Comparar como Postgres ──────────────────────────────────────────── */

const FECHA = /^\d{4}-\d{2}-\d{2}/;
const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** Instante de una fecha/hora; sin zona se toma como UTC (Supabase corre en UTC). */
function instante(s: string): number {
  let t = s.trim().replace(" ", "T");
  if (SOLO_FECHA.test(t)) return Date.parse(`${t}T00:00:00Z`);
  // `+00` → `+00:00` (Postgres a veces abrevia la zona)
  t = t.replace(/([+-]\d{2})$/, "$1:00");
  // Postgres da microsegundos y Safari (WKWebView) no los entiende: a milisegundos.
  t = t.replace(/(\.\d{3})\d+/, "$1");
  if (!/(Z|[+-]\d{2}:\d{2})$/.test(t)) t += "Z";
  return Date.parse(t);
}

/** -1/0/1, o null si no se pueden comparar (alguno es NULL o no casan los tipos). */
function comparar(a: unknown, b: string): number | null {
  if (a === null || a === undefined) return null;
  if (typeof a === "number") {
    const n = Number(b);
    if (b.trim() === "" || Number.isNaN(n)) return null;
    return a < n ? -1 : a > n ? 1 : 0;
  }
  if (typeof a === "boolean") {
    if (b !== "true" && b !== "false") return null;
    const bb = b === "true";
    return a === bb ? 0 : a ? 1 : -1;
  }
  if (typeof a === "string") {
    if (FECHA.test(a) && FECHA.test(b)) {
      // Columna `date` contra una hora: Postgres convierte el literal a fecha.
      if (SOLO_FECHA.test(a)) {
        const bf = b.slice(0, 10);
        return a < bf ? -1 : a > bf ? 1 : 0;
      }
      const x = instante(a);
      const y = instante(b);
      if (!Number.isNaN(x) && !Number.isNaN(y)) return x < y ? -1 : x > y ? 1 : 0;
    }
    return a < b ? -1 : a > b ? 1 : 0;
  }
  // json/arrays: solo igualdad textual
  const ja = JSON.stringify(a);
  return ja === b ? 0 : null;
}

function igual(a: unknown, b: string): boolean | null {
  if (a === null || a === undefined) return null;
  if (typeof a === "string" && !FECHA.test(a)) return a === b;
  const c = comparar(a, b);
  return c === null ? null : c === 0;
}

/** `(a,"b,c",d)` → ["a","b,c","d"] */
function listaIn(valor: string): string[] {
  if (!valor.startsWith("(") || !valor.endsWith(")")) throw new NoSe("in sin paréntesis");
  const dentro = valor.slice(1, -1);
  if (dentro === "") return [];
  const out: string[] = [];
  let actual = "";
  let comillas = false;
  for (let i = 0; i < dentro.length; i++) {
    const c = dentro[i];
    if (c === '"') {
      comillas = !comillas;
      continue;
    }
    if (c === "\\" && comillas && i + 1 < dentro.length) {
      actual += dentro[++i];
      continue;
    }
    if (c === "," && !comillas) {
      out.push(actual);
      actual = "";
      continue;
    }
    actual += c;
  }
  out.push(actual);
  return out;
}

function patron(valor: string, mayusculas: boolean): RegExp {
  const esc = valor.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/[*%]/g, ".*").replace(/_/g, ".");
  return new RegExp(`^${esc}$`, mayusculas ? "s" : "is");
}

/** `{a,b}` (array) o JSON → ¿a contiene b? */
function contiene(a: unknown, valor: string): boolean | null {
  if (a === null || a === undefined) return null;
  let b: unknown;
  if (Array.isArray(a) && valor.startsWith("{") && !valor.startsWith('{"')) {
    b = listaIn(`(${valor.slice(1, -1)})`);
    return (b as string[]).every((x) => (a as unknown[]).some((y) => String(y) === x));
  }
  try {
    b = JSON.parse(valor);
  } catch {
    throw new NoSe("cs con valor raro");
  }
  return contieneJson(a, b);
}

function contieneJson(a: unknown, b: unknown): boolean {
  if (Array.isArray(b)) {
    if (!Array.isArray(a)) return false;
    return b.every((x) => a.some((y) => contieneJson(y, x)));
  }
  if (b && typeof b === "object") {
    if (!a || typeof a !== "object" || Array.isArray(a)) return false;
    return Object.entries(b as Fila).every(([k, v]) => contieneJson((a as Fila)[k], v));
  }
  return a === b;
}

/** Lógica de tres valores, como SQL: true, false o null (desconocido). */
function evaluarCond(f: Extract<Filtro, { tipo: "cond" }>, fila: Fila): boolean | null {
  if (!(f.columna in fila)) throw new NoSe(`columna desconocida: ${f.columna}`);
  const a = fila[f.columna];
  let r: boolean | null;
  switch (f.op) {
    case "eq":
      r = igual(a, f.valor);
      break;
    case "neq": {
      const e = igual(a, f.valor);
      r = e === null ? null : !e;
      break;
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const c = comparar(a, f.valor);
      if (c === null) r = null;
      else r = f.op === "gt" ? c > 0 : f.op === "gte" ? c >= 0 : f.op === "lt" ? c < 0 : c <= 0;
      break;
    }
    case "like":
    case "ilike":
      r = a === null || a === undefined ? null : patron(f.valor, f.op === "like").test(String(a));
      break;
    case "is": {
      const v = f.valor.toLowerCase();
      if (v === "null") r = a === null || a === undefined;
      else if (v === "true") r = a === true;
      else if (v === "false") r = a === false;
      else if (v === "unknown") r = a === null || a === undefined;
      else throw new NoSe(`is.${f.valor}`);
      // IS no devuelve nunca NULL.
      return f.negado ? !r : r;
    }
    case "in": {
      const lista = listaIn(f.valor);
      if (a === null || a === undefined) r = null;
      else {
        let algunNulo = false;
        r = false;
        for (const x of lista) {
          const e = igual(a, x);
          if (e === true) {
            r = true;
            break;
          }
          if (e === null) algunNulo = true;
        }
        if (!r && algunNulo) r = null;
      }
      break;
    }
    case "cs":
      r = contiene(a, f.valor);
      break;
  }
  if (f.negado) return r === null ? null : !r;
  return r;
}

function evaluarFiltro(f: Filtro, fila: Fila): boolean | null {
  if (f.tipo === "cond") return evaluarCond(f, fila);
  let r: boolean | null;
  if (f.y) {
    r = true;
    for (const p of f.partes) {
      const v = evaluarFiltro(p, fila);
      if (v === false) {
        r = false;
        break;
      }
      if (v === null) r = null;
    }
  } else {
    r = false;
    for (const p of f.partes) {
      const v = evaluarFiltro(p, fila);
      if (v === true) {
        r = true;
        break;
      }
      if (v === null) r = null;
    }
  }
  if (f.negado) return r === null ? null : !r;
  return r;
}

/* ─── 4. Orden ───────────────────────────────────────────────────────────── */

interface Orden {
  columna: string;
  desc: boolean;
  nulosPrimero: boolean;
}

function analizarOrden(valor: string): Orden[] {
  return valor.split(",").map((p) => {
    if (p.includes("(")) throw new NoSe("orden por relación");
    const [columna, ...mods] = p.split(".");
    const desc = mods.includes("desc");
    // Postgres: ASC pone los NULL al final; DESC, al principio.
    const nulosPrimero = mods.includes("nullsfirst") ? true : mods.includes("nullslast") ? false : desc;
    return { columna, desc, nulosPrimero };
  });
}

function cmpValores(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return a === b ? 0 : a ? 1 : -1;
  if (typeof a === "string" && typeof b === "string") {
    if (FECHA.test(a) && FECHA.test(b) && !SOLO_FECHA.test(a) && !SOLO_FECHA.test(b)) {
      const x = instante(a);
      const y = instante(b);
      if (!Number.isNaN(x) && !Number.isNaN(y)) return x - y;
    }
    return a < b ? -1 : a > b ? 1 : 0;
  }
  const sa = JSON.stringify(a);
  const sb = JSON.stringify(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

function ordenar(filas: Fila[], orden: Orden[]): Fila[] {
  for (const o of orden) {
    if (filas.length && !(o.columna in filas[0])) throw new NoSe(`orden por columna desconocida: ${o.columna}`);
  }
  return filas
    .map((f, i) => ({ f, i }))
    .sort((x, y) => {
      for (const o of orden) {
        const a = x.f[o.columna];
        const b = y.f[o.columna];
        const na = a === null || a === undefined;
        const nb = b === null || b === undefined;
        if (na || nb) {
          if (na && nb) continue;
          return na ? (o.nulosPrimero ? -1 : 1) : o.nulosPrimero ? 1 : -1;
        }
        const c = cmpValores(a, b);
        if (c !== 0) return o.desc ? -c : c;
      }
      return x.i - y.i;
    })
    .map((x) => x.f);
}

/* ─── 5. Relaciones ──────────────────────────────────────────────────────── */

interface Enlace {
  destino: string;
  /** true = la FK está en el padre (muchos-a-uno → objeto). */
  haciaArriba: boolean;
  /** Columnas del padre y del hijo que tienen que casar, en orden. */
  colsPadre: string[];
  colsHijo: string[];
  /** Devuelve un objeto (y no una lista). */
  unico: boolean;
}

function fksDe(tabla: string): ClaveForanea[] {
  return RELACIONES[tabla]?.fks ?? [];
}

export function resolverEnlace(padre: string, emb: Pick<Incrustada, "nombre" | "pistas">): Enlace {
  const candidatos: Array<Enlace & { fk: ClaveForanea }> = [];
  const nombre = emb.nombre;
  const esRelacion = nombre in RELACIONES;

  // Hacia arriba: la FK está en el padre.
  for (const fk of fksDe(padre)) {
    const porTabla = esRelacion && fk.referencia === nombre;
    const porColumna = !esRelacion && fk.columnas.length === 1 && fk.columnas[0] === nombre;
    const porNombreFk = !esRelacion && fk.nombre === nombre;
    if (porTabla || porColumna || porNombreFk) {
      candidatos.push({
        fk,
        destino: fk.referencia,
        haciaArriba: true,
        colsPadre: fk.columnas,
        colsHijo: fk.columnasReferencia,
        unico: true,
      });
    }
  }
  // Hacia abajo: la FK está en el hijo y apunta al padre.
  if (esRelacion) {
    for (const fk of fksDe(nombre)) {
      if (fk.referencia !== padre) continue;
      candidatos.push({
        fk,
        destino: nombre,
        haciaArriba: false,
        colsPadre: fk.columnasReferencia,
        colsHijo: fk.columnas,
        unico: fk.unoAUno,
      });
    }
  }

  let elegidos = candidatos;
  for (const pista of emb.pistas) {
    elegidos = elegidos.filter(
      (c) => c.fk.nombre === pista || (c.fk.columnas.length === 1 && c.fk.columnas[0] === pista) || c.fk.columnas.includes(pista),
    );
  }
  if (elegidos.length === 0) throw new NoSe(`sin relación ${padre} → ${nombre}`);
  if (elegidos.length > 1) {
    // Varias FK con las mismas columnas al mismo destino (duplicadas por las vistas): da igual cuál.
    const firma = (c: Enlace) => `${c.destino}|${c.haciaArriba}|${c.colsPadre.join()}|${c.colsHijo.join()}`;
    if (new Set(elegidos.map(firma)).size > 1) throw new NoSe(`relación ambigua ${padre} → ${nombre}`);
  }
  const { fk: _fk, ...enlace } = elegidos[0];
  return enlace;
}

/* ─── 6. La consulta entera ──────────────────────────────────────────────── */

export interface ConsultaAnalizada {
  tabla: string;
  select: ElementoSelect[];
  /** Filtros por ruta de incrustación: "" = la tabla principal, "a" = la incrustada con alias a, "a.b"… */
  filtros: Map<string, Filtro[]>;
  ordenes: Map<string, Orden[]>;
  limites: Map<string, number>;
  desplazamientos: Map<string, number>;
}

const RESERVADOS = new Set(["select", "order", "limit", "offset", "on_conflict", "columns"]);

export function analizarConsulta(ruta: string): ConsultaAnalizada {
  const q = ruta.indexOf("?");
  const tabla = decodeURIComponent(q >= 0 ? ruta.slice(0, q) : ruta);
  if (!tabla || tabla.includes("/") || tabla.startsWith("rpc")) throw new NoSe("no es una tabla");
  const params = new URLSearchParams(q >= 0 ? ruta.slice(q + 1) : "");
  const select = analizarSelect(params.get("select") ?? "*");
  const filtros = new Map<string, Filtro[]>();
  const ordenes = new Map<string, Orden[]>();
  const limites = new Map<string, number>();
  const desplazamientos = new Map<string, number>();
  const anadir = (ruta: string, f: Filtro) => {
    const l = filtros.get(ruta) ?? [];
    l.push(f);
    filtros.set(ruta, l);
  };

  params.forEach((valor, clave) => {
    if (clave === "select") return;
    const trozos = clave.split(".");
    const ultimo = trozos[trozos.length - 1];
    // `rel.order`, `rel.limit`, `rel.or`, `rel.col`, `not.or`…
    let rutaEmb: string;
    let nombre: string;
    if (RESERVADOS.has(ultimo) || ultimo === "or" || ultimo === "and") {
      nombre = ultimo;
      rutaEmb = trozos.slice(0, -1).join(".");
      if (rutaEmb === "not") {
        rutaEmb = "";
        nombre = `not.${ultimo}`;
      } else if (rutaEmb.endsWith(".not")) {
        rutaEmb = rutaEmb.slice(0, -4);
        nombre = `not.${ultimo}`;
      }
    } else {
      nombre = ultimo;
      rutaEmb = trozos.slice(0, -1).join(".");
    }
    switch (nombre) {
      case "order":
        ordenes.set(rutaEmb, analizarOrden(valor));
        return;
      case "limit": {
        const n = Number(valor);
        if (!Number.isInteger(n) || n < 0) throw new NoSe("limit raro");
        limites.set(rutaEmb, n);
        return;
      }
      case "offset": {
        const n = Number(valor);
        if (!Number.isInteger(n) || n < 0) throw new NoSe("offset raro");
        desplazamientos.set(rutaEmb, n);
        return;
      }
      case "on_conflict":
      case "columns":
        throw new NoSe("parámetro de escritura");
      case "or":
      case "and":
      case "not.or":
      case "not.and":
        anadir(rutaEmb, analizarLogico(nombre.endsWith("and"), nombre.startsWith("not."), valor));
        return;
      default:
        anadir(rutaEmb, analizarCondicion(nombre, valor));
    }
  });
  return { tabla, select, filtros, ordenes, limites, desplazamientos };
}

/** Todas las tablas que hacen falta para contestar (la principal y las incrustadas). */
export function tablasNecesarias(c: ConsultaAnalizada): string[] {
  const out = new Set<string>([c.tabla]);
  const recorrer = (tabla: string, sel: ElementoSelect[]) => {
    for (const e of sel) {
      if (e.tipo !== "incrustada") continue;
      const enlace = resolverEnlace(tabla, e);
      out.add(enlace.destino);
      recorrer(enlace.destino, e.hijos);
    }
  };
  recorrer(c.tabla, c.select);
  return Array.from(out);
}

export type FuenteDeFilas = (tabla: string) => Fila[] | null;

/** Índices por (tabla, columnas) para que incrustar no sea cuadrático. */
class Indices {
  private cache = new Map<string, Map<string, Fila[]>>();
  constructor(private fuente: FuenteDeFilas) {}
  porColumnas(tabla: string, cols: string[]): Map<string, Fila[]> {
    const k = `${tabla}|${cols.join(",")}`;
    let m = this.cache.get(k);
    if (m) return m;
    m = new Map();
    const filas = this.fuente(tabla);
    if (!filas) throw new NoSe(`tabla sin copia: ${tabla}`);
    for (const f of filas) {
      const clave = cols.map((c) => String(f[c] ?? "\u0000")).join("\u0001");
      const l = m.get(clave);
      if (l) l.push(f);
      else m.set(clave, [f]);
    }
    this.cache.set(k, m);
    return m;
  }
}

function clavePadre(fila: Fila, cols: string[]): string | null {
  const vals = cols.map((c) => fila[c]);
  if (vals.some((v) => v === null || v === undefined)) return null;
  return vals.map((v) => String(v)).join("\u0001");
}

function aplicarFiltros(filas: Fila[], filtros: Filtro[] | undefined): Fila[] {
  if (!filtros || filtros.length === 0) return filas;
  return filas.filter((f) => filtros.every((x) => evaluarFiltro(x, f) === true));
}

interface Contexto {
  c: ConsultaAnalizada;
  indices: Indices;
}

/**
 * Proyecta una fila según el select. Devuelve null si una incrustada `!inner`
 * se quedó vacía (la fila no entra en el resultado).
 */
function proyectar(ctx: Contexto, tabla: string, ruta: string, sel: ElementoSelect[], fila: Fila): Fila | null {
  const out: Fila = {};
  for (const e of sel) {
    if (e.tipo === "todas") {
      Object.assign(out, fila);
    } else if (e.tipo === "columna") {
      if (!(e.nombre in fila)) throw new NoSe(`columna desconocida: ${tabla}.${e.nombre}`);
      out[e.alias] = e.json ? seguirJson(fila[e.nombre], e.json) : fila[e.nombre] ?? null;
    }
  }
  for (const e of sel) {
    if (e.tipo !== "incrustada") continue;
    const enlace = resolverEnlace(tabla, e);
    const rutaHija = ruta ? `${ruta}.${e.alias}` : e.alias;
    const clave = clavePadre(fila, enlace.colsPadre);
    let hijos = clave === null ? [] : ctx.indices.porColumnas(enlace.destino, enlace.colsHijo).get(clave) ?? [];
    hijos = aplicarFiltros(hijos, ctx.c.filtros.get(rutaHija) ?? (e.alias !== e.nombre ? ctx.c.filtros.get(ruta ? `${ruta}.${e.nombre}` : e.nombre) : undefined));
    const orden = ctx.c.ordenes.get(rutaHija);
    if (orden) hijos = ordenar(hijos, orden);
    const proyectados: Fila[] = [];
    for (const h of hijos) {
      const p = proyectar(ctx, enlace.destino, rutaHija, e.hijos, h);
      if (p) proyectados.push(p);
    }
    let lista = proyectados;
    const desde = ctx.c.desplazamientos.get(rutaHija) ?? 0;
    const lim = ctx.c.limites.get(rutaHija);
    if (desde || lim !== undefined) lista = lista.slice(desde, lim !== undefined ? desde + lim : undefined);

    if (enlace.unico) {
      const v = lista[0] ?? null;
      if (e.interior && v === null) return null;
      out[e.alias] = v;
    } else {
      if (e.interior && lista.length === 0) return null;
      out[e.alias] = lista;
    }
  }
  return out;
}

/** `col->a->>b`: baja por el JSON; `->>` lo devuelve como texto, como Postgres. */
function seguirJson(valor: unknown, camino: NonNullable<Columna["json"]>): unknown {
  let v: unknown = valor;
  for (const paso of camino) {
    if (v === null || v === undefined) return null;
    if (Array.isArray(v) && /^\d+$/.test(paso.clave)) v = v[Number(paso.clave)];
    else if (typeof v === "object" && !Array.isArray(v)) v = (v as Fila)[paso.clave];
    else return null;
    if (v === undefined) return null;
  }
  if (camino[camino.length - 1].comoTexto && v !== null && typeof v !== "string") return JSON.stringify(v);
  return v ?? null;
}

export interface CabecerasConsulta {
  metodo: string;
  accept?: string;
  prefer?: string;
  range?: string;
}

export interface RespuestaLocal {
  status: number;
  body: string | null;
  headers: Record<string, string>;
}

/**
 * Contesta la consulta con las filas de la copia. `null` si no sabe hacerlo
 * con seguridad (sintaxis que no entiende, tabla sin copia, relación ambigua).
 */
export function contestarConsulta(ruta: string, cab: CabecerasConsulta, fuente: FuenteDeFilas): RespuestaLocal | null {
  try {
    const metodo = cab.metodo.toUpperCase();
    if (metodo !== "GET" && metodo !== "HEAD") return null;
    const c = analizarConsulta(ruta);
    const base = fuente(c.tabla);
    if (!base) return null;
    const ctx: Contexto = { c, indices: new Indices(fuente) };

    let filas = aplicarFiltros(base, c.filtros.get(""));
    const orden = c.ordenes.get("");
    if (orden) filas = ordenar(filas, orden);

    // Proyectar ANTES de cortar: un `!inner` vacío saca la fila y no debe gastar plaza del límite.
    const proyectadas: Fila[] = [];
    for (const f of filas) {
      const p = proyectar(ctx, c.tabla, "", c.select, f);
      if (p) proyectadas.push(p);
    }

    let desde = c.desplazamientos.get("") ?? 0;
    let lim = c.limites.get("");
    const rango = cab.range?.match(/^(\d+)-(\d*)$/);
    if (rango) {
      desde = Number(rango[1]);
      if (rango[2] !== "") lim = Number(rango[2]) - desde + 1;
    }
    const total = proyectadas.length;
    const pagina = proyectadas.slice(desde, lim !== undefined ? desde + lim : undefined);

    const headers: Record<string, string> = { "content-type": "application/json; charset=utf-8" };
    const conteo = /count=(exact|planned|estimated)/.test(cab.prefer ?? "");
    const fin = desde + pagina.length - 1;
    headers["content-range"] = `${pagina.length ? `${desde}-${fin}` : "*"}/${conteo ? total : "*"}`;

    if ((cab.accept ?? "").includes("application/vnd.pgrst.object+json")) {
      if (pagina.length !== 1) {
        return {
          status: 406,
          headers,
          body: JSON.stringify({
            code: "PGRST116",
            details: `The result contains ${pagina.length} rows`,
            hint: null,
            message: "Cannot coerce the result to a single JSON object",
          }),
        };
      }
      return { status: 200, headers, body: metodo === "HEAD" ? null : JSON.stringify(pagina[0]) };
    }
    return { status: 200, headers, body: metodo === "HEAD" ? null : JSON.stringify(pagina) };
  } catch (e) {
    if (e instanceof NoSe) return null;
    // Cualquier otra cosa también es «no sé»: nunca se contesta a medias.
    return null;
  }
}

/** Para las pruebas y el diagnóstico: ¿por qué no sabe contestar? */
export function porQueNo(ruta: string, fuente: FuenteDeFilas): string | null {
  try {
    const c = analizarConsulta(ruta);
    for (const t of tablasNecesarias(c)) if (!fuente(t)) return `tabla sin copia: ${t}`;
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}
